import { FC, useEffect } from "react";
import { X } from "lucide-react";
import { Citation } from "@/constants/citation.constant";

/**
 * Props for the RightSideModal component
 */
interface Props {
  /** Whether the modal is open */
  open: boolean;
  /** Citation reference to display */
  reference: Citation | null;
  /** Whether reference is loading. Defaults to false. */
  loading?: boolean;
  /** Callback fired when modal is closed */
  onClose: () => void;
}

/**
 * RightSideModal component displays a right-side panel with citation preview.
 * Shows citation name, URL, and embedded iframe preview. Closes on Escape key
 * or overlay click.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {boolean} props.open - Whether modal is open
 * @param {Citation | null} props.reference - Citation reference to display
 * @param {boolean} [props.loading=false] - Whether reference is loading
 * @param {() => void} props.onClose - Close callback
 * @returns {JSX.Element | null} Right-side panel with citation preview or null if not open
 * 
 * @example
 * ```tsx
 * <RightSideModal
 *   open={isOpen}
 *   reference={citation}
 *   onClose={handleClose}
 * />
 * ```
 */
export const RightSideModal: FC<Props> = ({ open, reference, loading, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      aria-label="Citation preview"
    >
      {/* overlay */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* right panel */}
      <aside className="ml-auto relative z-10 w-full md:w-[520px] max-w-full h-full bg-white dark:bg-[#0b0b0c] shadow-lg overflow-auto fixed md:relative inset-0 md:inset-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Source preview</h3>
          <button aria-label="Close" onClick={onClose} className="p-2">
            <X size={18} />
          </button>
        </div>

        <div className="p-4">
          {loading && <div className="text-sm text-gray-500">Loading...</div>}

          {!loading && !reference && (
            <div className="text-sm text-gray-500">No reference selected</div>
          )}

          {!loading && reference && (
            <>
              <div className="mb-3">
                <div className="text-sm font-medium">
                  {reference.name || reference.content?.split('\n')[0] || 'Citation'}
                </div>
                <a
                  className="text-xs text-primary-500 break-all"
                  href={reference.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {reference.url}
                </a>
              </div>

              {/* show iframe when the link allows embedding; fallback is a link */}
              <div className="border rounded overflow-hidden h-[60svh]">
                <iframe
                  src={reference.url}
                  title={reference.name || reference.content?.split('\n')[0] || "reference"}
                  className="w-full h-full"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
};